(() => {
  const root = () => window.Shopify?.routes?.root || '/';
  const cartUrl = (path) => `${root()}cart/${path}`;

  async function request(url, options = {}) {
    const response = await fetch(url, options);
    const responseText = await response.text();
    let content = {};

    try {
      content = responseText ? JSON.parse(responseText) : {};
    } catch (_error) {
      content = {};
    }

    if (!response.ok) {
      throw new Error(content.description || content.message || content.error || `Cart update failed (${response.status}).`);
    }

    return content;
  }

  function bundledSections() {
    const sections = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections }
    }));
    return [...sections];
  }

  async function currentCart() {
    return request(`${root()}cart.js?t=${Date.now()}`, { cache: 'no-store' });
  }

  function dispatchCartChange(cart, baseEvent) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart }
    }));
  }

  async function updateSubscription(toggle) {
    const wrapper = toggle.closest('[data-cloudless-subscription]');
    if (!wrapper || wrapper.dataset.loading === 'true') return;

    const error = wrapper.querySelector('[data-subscription-error]');
    const shouldSubscribe = toggle.checked;
    const sections = bundledSections();
    wrapper.dataset.loading = 'true';
    toggle.disabled = true;
    if (error) error.hidden = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const cart = await currentCart();
      const lineKey = wrapper.dataset.lineKey;
      let itemIndex = cart.items.findIndex((line) => line.key === lineKey);

      if (itemIndex === -1) {
        itemIndex = cart.items.findIndex((line) => (
          String(line.variant_id) === wrapper.dataset.variantId
          && Boolean(line.selling_plan_allocation) === !shouldSubscribe
          && line.final_price > 0
        ));
      }

      const item = cart.items[itemIndex];

      if (!item) throw new Error('Dit product staat niet meer in je winkelmandje.');

      const sellingPlanId = Number(wrapper.dataset.sellingPlanId);
      if (shouldSubscribe && (!Number.isInteger(sellingPlanId) || sellingPlanId <= 0)) {
        throw new Error('Voor dit product is geen geldig abonnement beschikbaar.');
      }

      const payload = {
        line: itemIndex + 1,
        quantity: item.quantity,
        selling_plan: shouldSubscribe ? sellingPlanId : null
      };

      if (sections.length > 0) payload.sections = sections.join(',');

      const updatedCart = await request(cartUrl('change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }

      dispatchCartChange(updatedCart, 'cloudless:subscription-change');
    } catch (exception) {
      toggle.checked = !shouldSubscribe;
      if (error) {
        error.textContent = exception.message;
        error.hidden = false;
      }
    } finally {
      wrapper.dataset.loading = 'false';
      toggle.disabled = false;
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  function sameSellingPlan(line, sellingPlanId) {
    const linePlanId = line.selling_plan_allocation?.selling_plan?.id;
    return sellingPlanId ? String(linePlanId || '') === String(sellingPlanId) : !linePlanId;
  }

  function sameProperties(lineProperties, targetProperties) {
    const normalizedEntries = (properties) => Object.entries(properties || {})
      .filter(([, value]) => value !== null && value !== '')
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

    return JSON.stringify(normalizedEntries(lineProperties)) === JSON.stringify(normalizedEntries(targetProperties));
  }

  async function prepareKachingUpgrade(sourceLine, targetVariantId, subscribed) {
    if (!window.CloudlessKachingUpgrade) return null;
    try {
      return await window.CloudlessKachingUpgrade.prepare(sourceLine, targetVariantId, { subscribed });
    } catch (exception) {
      console.error('Kaching upgrade data unavailable, upgrading without bundle gifts.', exception);
      return null;
    }
  }

  async function addKachingGifts(gifts, sections) {
    if (!gifts?.length) return null;
    try {
      await request(cartUrl('add.js?kaching_bundles=true&partial=true'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: gifts })
      });
      const cart = await currentCart();
      if (!sections.length) return cart;
      const rendered = await request(`${root()}?sections=${encodeURIComponent(sections.join(','))}`);
      return { ...cart, sections: rendered };
    } catch (exception) {
      console.error('Kaching gifts could not be added after the upgrade.', exception);
      return null;
    }
  }

  async function upgradeToThreeMonths(button) {
    const card = button.closest('[data-cart-upgrade-card]');
    if (!card || button.disabled) return;

    const error = card.querySelector('[data-cart-upgrade-error]');
    const targetVariantId = Number(button.dataset.targetVariantId);
    const targetSellingPlanId = Number(button.dataset.targetSellingPlanId) || null;
    const sections = bundledSections();
    let targetBefore = null;
    let addedLineKey = null;
    let added = false;

    if (error) error.hidden = true;
    button.disabled = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      if (!Number.isInteger(targetVariantId) || targetVariantId <= 0) {
        throw new Error('Deze variant is niet beschikbaar.');
      }

      const cart = await currentCart();
      const sourceLine = cart.items.find((line) => line.key === button.dataset.lineKey);
      if (!sourceLine) throw new Error('Dit product staat niet meer in je winkelmandje.');

      const kachingUpgrade = await prepareKachingUpgrade(sourceLine, targetVariantId, Boolean(targetSellingPlanId));
      const properties = { ...(kachingUpgrade?.mainProperties || sourceLine.properties || {}) };
      delete properties._selling_plan_id;
      const targetItem = { id: targetVariantId, quantity: 1, properties };
      if (targetSellingPlanId) targetItem.selling_plan = targetSellingPlanId;

      targetBefore = cart.items.find((line) => (
        line.variant_id === targetVariantId
        && sameSellingPlan(line, targetSellingPlanId)
        && sameProperties(line.properties, properties)
      )) || null;

      const addPayload = { items: [targetItem] };
      if (sections.length) addPayload.sections = sections.join(',');
      const addedCart = await request(cartUrl('add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addPayload)
      });
      addedLineKey = addedCart.items?.[0]?.key || null;
      added = true;

      const changePayload = { id: sourceLine.key, quantity: 0 };
      if (sections.length) changePayload.sections = sections.join(',');
      let updatedCart = await request(cartUrl('change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changePayload)
      });
      updatedCart = await addKachingGifts(kachingUpgrade?.gifts, sections) || updatedCart;

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }

      dispatchCartChange(updatedCart, 'cloudless:bundle-upgrade');
    } catch (exception) {
      if (added) {
        try {
          const rollbackCart = await currentCart();
          const targetLine = rollbackCart.items.find((line) => line.key === addedLineKey) || rollbackCart.items.find((line) => (
            line.variant_id === targetVariantId
            && sameSellingPlan(line, targetSellingPlanId)
            && sameProperties(line.properties, targetBefore?.properties)
          ));
          if (targetLine) {
            await request(cartUrl('change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: targetLine.key,
                quantity: targetBefore?.key === targetLine.key ? targetBefore.quantity : 0
              })
            });
          }
        } catch (_rollbackError) {
          window.location.reload();
          return;
        }
      }

      if (error) {
        error.textContent = exception.message;
        error.hidden = false;
      }
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: exception.message } }));
      button.disabled = false;
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  // Reads the JUO plan map rendered by sections/juo-selling-plans.liquid. Only the template's text is parsed (any
  // markup an app or Shopify injects is ignored), and one retry covers a response that came back as an HTML page.
  async function sellingPlanMap() {
    let detail = '';
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      const response = await fetch(`${root()}?section_id=juo-selling-plans&t=${Date.now()}`, { cache: 'no-store' });
      const html = response.ok ? await response.text() : '';
      const template = new DOMParser().parseFromString(html, 'text/html').querySelector('#juo-selling-plans-map');
      const text = (template?.content?.textContent ?? template?.textContent ?? '').trim();
      try {
        if (template) return JSON.parse(text || '{}');
      } catch (_error) {
        console.error('JUO plan map is not valid JSON', { attempt, status: response.status, text: text.slice(0, 500) });
      }
      if (!template) console.error('JUO plan map missing', { attempt, status: response.status, body: html.slice(0, 300) });
      detail = `${response.status} ${(template ? text : html).replace(/\s+/g, ' ').slice(0, 40)}`;
    }
    // The short detail lets a shopper's screenshot show what came back instead of the plan map.
    throw new Error(`De lokale betaalopties konden niet worden geladen. Probeer het opnieuw. (${detail})`);
  }

  async function addUpsell(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const variantId = Number(button.dataset.variantId);
    if (!Number.isInteger(variantId) || variantId <= 0) return;
    button.setAttribute('aria-busy', 'true');
    try {
      const sections = bundledSections();
      const payload = { items: [{ id: variantId, quantity: 1, properties: { _source: 'cart-upsell' } }] };
      if (sections.length) payload.sections = sections.join(',');
      const added = await request(cartUrl('add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }
      const cart = await currentCart();
      cart.sections = added.sections;
      dispatchCartChange(cart, 'variant:add');
    } catch (exception) {
      document.dispatchEvent(new CustomEvent('toast:show', {
        detail: { message: exception.message, tone: 'error' }
      }));
      button.removeAttribute('aria-busy');
    }
  }

  const CHECKOUT_BUTTON_SELECTOR = '.cl-halo-checkout';

  function resetCheckoutButtons() {
    document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR).forEach((button) => {
      button.classList.remove('is-checking');
      button.removeAttribute('aria-disabled');
      if (button.matches('[data-juo-checkout]')) button.disabled = false;
    });
  }

  function handleCheckoutSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    if (!submitter?.matches(CHECKOUT_BUTTON_SELECTOR)) return;
    if (form.querySelector('.is-checking')) {
      event.preventDefault();
      return;
    }
    submitter.classList.add('is-checking');
    submitter.setAttribute('aria-disabled', 'true');
    window.setTimeout(resetCheckoutButtons, 10000);
  }

  // JUO's local-payment plans do not count towards the automatic Mystery Gift discount, so a gift that is free
  // in the cart is swapped for its unlisted €0 copy before the JUO checkout, and back when the shopper returns.
  function giftVariants() {
    try {
      const config = JSON.parse(document.getElementById('cl-mystery-gift-config')?.textContent || '{}');
      return { gift: Number(config.variant_id) || null, juoGift: Number(config.juo_variant_id) || null };
    } catch (error) {
      console.error('Mystery Gift settings could not be read', error);
      return { gift: null, juoGift: null };
    }
  }

  async function checkoutWithJuo(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('is-checking');

    try {
      const cart = await currentCart();
      const subscriptionItems = cart.items.filter((item) => item.selling_plan_allocation);

      if (!subscriptionItems.length) {
        window.location.assign(`${root()}checkout`);
        return;
      }

      const planMap = await sellingPlanMap();
      const missingPlan = subscriptionItems.some((item) => !planMap[item.key]);
      if (missingPlan) throw new Error('De lokale betaaloptie is niet beschikbaar voor deze abonnementskeuze.');

      const { gift, juoGift } = giftVariants();
      const items = cart.items.map((item) => {
        const subscribed = Boolean(item.selling_plan_allocation);
        const properties = { ...(item.properties || {}) };
        const freeGift = juoGift && item.variant_id === gift && item.final_line_price === 0;
        const payload = {
          id: freeGift ? juoGift : item.variant_id,
          quantity: item.quantity,
          properties
        };

        if (subscribed) {
          properties._selling_plan_id = String(item.selling_plan_allocation.selling_plan.id);
          payload.selling_plan = Number(planMap[item.key]);
        }

        return payload;
      });

      await request(cartUrl('clear.js'), { method: 'POST' });
      await request(cartUrl('add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: cart.attributes, items })
      });

      window.location.assign(`${root()}checkout?skip_shop_pay=true`);
    } catch (exception) {
      button.disabled = false;
      button.classList.remove('is-checking');
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: exception.message } }));
    }
  }

  async function restoreRecurringPlans() {
    const cart = await currentCart();
    const { gift, juoGift } = giftVariants();
    const isJuoGift = (item) => juoGift && item.variant_id === juoGift;
    const shouldRestore = cart.items.some((item) => item.properties?._selling_plan_id || isJuoGift(item));
    if (!shouldRestore) return;

    const items = cart.items.map((item) => {
      const properties = { ...(item.properties || {}) };
      const originalPlan = properties._selling_plan_id;
      delete properties._selling_plan_id;

      const payload = {
        id: isJuoGift(item) && gift ? gift : item.variant_id,
        quantity: item.quantity,
        properties
      };
      if (originalPlan) payload.selling_plan = Number(originalPlan);
      return payload;
    });

    const sections = bundledSections();
    const payload = { attributes: cart.attributes, items };
    if (sections.length) payload.sections = sections.join(',');
    await request(cartUrl('clear.js'), { method: 'POST' });
    const added = await request(cartUrl('add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    const restored = await currentCart();
    restored.sections = added.sections;
    dispatchCartChange(restored, 'cloudless:juo-restore');
  }

  document.addEventListener('change', (event) => {
    if (event.target.matches('.cl-subscription-toggle__input')) updateSubscription(event.target);
  });

  document.addEventListener('submit', handleCheckoutSubmit);
  window.addEventListener('pageshow', resetCheckoutButtons);

  // "Maak je routine compleet" can be collapsed; the state lives on <html> (survives drawer re-renders) and in
  // sessionStorage (survives page loads during the visit). Default: expanded.
  const UPSELLS_COLLAPSED_KEY = 'cl-upsells-collapsed';
  function setUpsellsCollapsed(collapsed, remember = true) {
    document.documentElement.classList.toggle('cl-upsells-collapsed', collapsed);
    document.querySelectorAll('[data-cart-upsells-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', String(!collapsed)));
    if (!remember) return;
    try {
      window.sessionStorage.setItem(UPSELLS_COLLAPSED_KEY, collapsed ? 'true' : 'false');
    } catch (error) {
      console.warn('Cloudless cart: could not remember the upsell state', error);
    }
  }
  const upsellsCollapsed = () => document.documentElement.classList.contains('cl-upsells-collapsed');
  try {
    setUpsellsCollapsed(window.sessionStorage.getItem(UPSELLS_COLLAPSED_KEY) === 'true', false);
  } catch (error) {
    setUpsellsCollapsed(false, false);
  }
  // Re-rendered cart sections come back with aria-expanded="true": correct them to the remembered state.
  new MutationObserver(() => {
    const expanded = String(!upsellsCollapsed());
    document.querySelectorAll('[data-cart-upsells-toggle]').forEach((toggle) => {
      if (toggle.getAttribute('aria-expanded') !== expanded) toggle.setAttribute('aria-expanded', expanded);
    });
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    const upsellsToggle = event.target.closest('[data-cart-upsells-toggle]');
    if (upsellsToggle) {
      setUpsellsCollapsed(!upsellsCollapsed());
      return;
    }

    const dismissButton = event.target.closest('[data-cart-upgrade-dismiss]');
    if (dismissButton) {
      event.preventDefault();
      dismissButton.closest('[data-cart-upgrade-card]')?.classList.add('is-dismissed');
      return;
    }

    const upgradeButton = event.target.closest('[data-cart-upgrade]');
    if (upgradeButton) {
      event.preventDefault();
      upgradeToThreeMonths(upgradeButton);
      return;
    }

    const upsellButton = event.target.closest('[data-cart-upsell-add]');
    if (upsellButton) {
      event.preventDefault();
      addUpsell(upsellButton);
      return;
    }

    const juoButton = event.target.closest('[data-juo-checkout]');
    if (juoButton) {
      event.preventDefault();
      checkoutWithJuo(juoButton);
    }
  });

  let removingOrphanedGifts = false;
  async function removeOrphanedGifts(cart) {
    const updates = window.CloudlessKachingUpgrade?.orphanedGiftUpdates(cart);
    if (removingOrphanedGifts || !updates || !Object.keys(updates).length) return;

    removingOrphanedGifts = true;
    try {
      const sections = bundledSections();
      const payload = { updates };
      if (sections.length) payload.sections = sections.join(',');
      const updatedCart = await request(cartUrl('update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }
      dispatchCartChange(updatedCart, 'cloudless:gift-cleanup');
    } catch (exception) {
      console.error('Could not remove gifts whose bundle product was removed.', exception);
    } finally {
      removingOrphanedGifts = false;
    }
  }

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.baseEvent === 'cloudless:gift-cleanup') return;
    const cart = event.detail?.cart;
    if (cart?.items) removeOrphanedGifts(cart);
    else currentCart().then(removeOrphanedGifts).catch((exception) => console.error(exception));
  });

  const init = () => {
    restoreRecurringPlans().catch(() => {});
    currentCart().then(removeOrphanedGifts).catch((exception) => console.error(exception));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  // Back from checkout: the browser shows the page from bfcache or its HTTP cache with the cart as it was.
  // Some browsers also answer the theme's own drawer refresh from the cache, so fetch the drawer uncached here.
  async function refreshDrawerUncached() {
    const drawer = document.querySelector('cart-drawer');
    const section = drawer?.closest('.shopify-section');
    if (!drawer || !section) return;
    const sectionId = section.id.replace('shopify-section-', '');
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}&_=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`cart drawer ${response.status}`);
    const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('cart-drawer');
    if (!fresh) return;
    drawer.replaceChildren(...fresh.childNodes);
    drawer.dispatchEvent(new CustomEvent('cart-drawer:refreshed', { bubbles: true }));
  }
  window.addEventListener('pageshow', (event) => {
    const navigation = performance.getEntriesByType?.('navigation')?.[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    // After the theme's own refresh, so the uncached version wins
    setTimeout(() => refreshDrawerUncached().catch((error) => console.warn('Cloudless cart: drawer refresh failed', error)), 300);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) restoreRecurringPlans().catch(() => {});
  });
})();
